import React, { forwardRef, useEffect, useId, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { ArrowDown, ArrowUp, BookOpen, Check, Copy, ExternalLink, Pencil, Plus, Trash2 } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import apiClient from '../services/apiClient';
import authService from '../services/AuthService';
import { getLanguage, t } from '../i18n';
import CoverUploadWidget from './CoverUploadWidget';
import CardBuilderDialog from '../features/import-note/components/CardBuilderDialog';
import CardDraftPreview from '../features/import-note/components/CardDraftPreview';
import { ALL_CARD_TEMPLATES, CARD_DRAFT_TYPES, getCardTemplate } from '../features/import-note/composer/cardTemplates';
import { validateComposerContent } from '../features/import-note/composer/composerValidation';
import { formatComposerError } from '../features/import-note/composer/composerMessages';
import { cardIdentity, cardPayload, cardPreview, describeCardChanges, editableCardContent } from './noteEditorModel';
import { editorText } from './noteEditorText';
import { useNavigationGuard } from './NavigationGuard';
import ConfirmDialog from './ConfirmDialog';
import '../features/import-note/components/NoteComposerPage.css';
import './NoteEditor.css';

const LOCALES = ['en-US', 'ko-KR', 'ja-JP', 'zh-CN', 'es-ES'];
const MAX_CARDS = 200;
const MAX_LANGUAGE_DETECTION_CARDS = 200;

function EditorDialog({ title, children, onCancel }) {
  const dialogRef = useRef(null);
  const titleId = useId();
  useEffect(() => {
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialogRef.current?.focus();
    const keydown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onCancel();
      }
      if (event.key !== 'Tab') return;
      const focusable = Array.from(dialogRef.current?.querySelectorAll('button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), a[href]') || []);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first) { event.preventDefault(); return; }
      if (event.shiftKey && (document.activeElement === first || document.activeElement === dialogRef.current)) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || document.activeElement === dialogRef.current)) {
        event.preventDefault(); first.focus();
      }
    };
    document.addEventListener('keydown', keydown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', keydown);
      previousFocus?.focus?.();
    };
  }, [onCancel]);
  return <div className="composer-modal-overlay">
    <section ref={dialogRef} tabIndex={-1} className="note-editor-dialog" role="dialog" aria-modal="true" aria-labelledby={titleId}>
      <h2 id={titleId}>{title}</h2>{children}
    </section>
  </div>;
}

/** Shared editor: only card content and stable IDs leave this component, never learning snapshots. */
function NoteEditor({
  initialNote = {}, onSave, onSaved, onCancel, mode = 'create', destination = 'personal',
  busy = false, appendOnly = false, currentCoverPreview = null,
  initialEditingCardId = null,
  studyKind = initialNote.studyKind || 'standard',
}, ref) {
  const language = getLanguage();
  const isVocabulary = studyKind === 'vocabulary';
  const text = (key) => isVocabulary && key === 'cardLimit' ? t('vocabulary.cardLimit', language) : editorText(key, language);
  const fieldId = useId();
  const isMarket = destination === 'market';
  const localesLocked = !isMarket && !isVocabulary && mode === 'edit';
  const [originalCards] = useState(() => (initialNote.cards || []).map((card, index) => ({
    ...editableCardContent(card),
    ...(card.id || card._id ? { id: String(card.id || card._id) } : { clientId: card.clientId || `original-${index}` }),
  })));
  const [cards, setCards] = useState(originalCards);
  const cardLimit = isVocabulary ? 30000 : mode === 'edit' && !isMarket ? Math.max(MAX_CARDS, originalCards.length) : MAX_CARDS;
  const [form, setForm] = useState(() => ({
    noteTitle: initialNote.noteTitle || initialNote.title || initialNote.name || '',
    description: initialNote.description || '',
    difficulty: initialNote.difficulty ?? '',
    questionLocale: initialNote.questionLocale || (isMarket ? (language === 'ko' ? 'ko-KR' : 'en-US') : 'ko-KR'),
    learningLocale: initialNote.learningLocale || 'en-US',
    isEnabled: initialNote.isEnabled ?? isVocabulary,
  }));
  const initialForm = useRef(form);
  const clientSequence = useRef(0);
  const [clientPrefix] = useState(() => uuidv4());
  const lastSaveOperation = useRef(null);
  const localeEdited = useRef({ question: false, learning: false });
  const detectionSequence = useRef(0);
  const savingRef = useRef(false);
  const [bulkText, setBulkText] = useState('');
  const [builder, setBuilder] = useState(() => {
    const card = isVocabulary && mode === 'edit' && originalCards.find(value => cardIdentity(value) === initialEditingCardId);
    return card ? { card } : null;
  });
  const [deleteCandidate, setDeleteCandidate] = useState(null);
  const [reviewing, setReviewing] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [saving, setSaving] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [coverFile, setCoverFile] = useState(null);
  const [removeCover, setRemoveCover] = useState(false);
  const [copied, setCopied] = useState(false);
  const [cardPage, setCardPage] = useState(() => Math.max(0, Math.floor(originalCards.findIndex(card => cardIdentity(card) === initialEditingCardId) / 50)));
  const [reviewPage, setReviewPage] = useState(0);
  const moveFocus = useRef(null);
  const moveButtons = useRef(new Map());
  const originalIds = useMemo(() => new Set(originalCards.map(cardIdentity)), [originalCards]);
  const currentIds = useMemo(() => new Set(cards.map(cardIdentity)), [cards]);
  const orderChanged = isVocabulary && originalCards.filter(card => currentIds.has(cardIdentity(card))).map(cardIdentity).join('|') !== cards.filter(card => originalIds.has(cardIdentity(card))).map(cardIdentity).join('|');
  const changes = useMemo(() => describeCardChanges(originalCards, cards), [originalCards, cards]);
  const reviewChanges = useMemo(() => ['added', 'modified', 'deleted'].flatMap(kind => changes[kind].map(card => ({ kind, card }))), [changes]);
  const reviewPageCount = Math.max(1, Math.ceil(reviewChanges.length / 50));
  const safeReviewPage = Math.min(reviewPage, reviewPageCount - 1);
  const visibleReviewChanges = isVocabulary ? reviewChanges.slice(safeReviewPage * 50, (safeReviewPage + 1) * 50) : reviewChanges;
  const pending = busy || saving;
  const dirty = Boolean(builder || bulkText.trim() || coverFile || removeCover || JSON.stringify(form) !== JSON.stringify(initialForm.current) || changes.added.length || changes.modified.length || changes.deleted.length || orderChanged);
  const navigation = useNavigationGuard(dirty);

  useEffect(() => () => { detectionSequence.current += 1; }, []);
  useEffect(() => {
    if (!moveFocus.current) return;
    const { identity, amount } = moveFocus.current;
    const preferred = moveButtons.current.get(`${identity}:${amount}`);
    const alternate = moveButtons.current.get(`${identity}:${-amount}`);
    const target = preferred?.disabled ? alternate : preferred;
    if (target?.isConnected) { target.focus(); moveFocus.current = null; }
  }, [cards, cardPage]);

  const update = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
    if (key === 'questionLocale') localeEdited.current.question = true;
    if (key === 'learningLocale') localeEdited.current.learning = true;
    setError('');
  };

  const suggestLocales = async (sourceCards = cards) => {
    if (localesLocked || appendOnly || !sourceCards.length) return;
    const sequence = ++detectionSequence.current;
    setDetecting(true);
    setNotice('');
    try {
      const result = await apiClient.detectNoteLocales(sourceCards.slice(0, MAX_LANGUAGE_DETECTION_CARDS).map(editableCardContent));
      if (sequence !== detectionSequence.current) return;
      setForm((current) => ({
        ...current,
        ...(!localeEdited.current.question && LOCALES.includes(result?.suggestedQuestionLocale) && result?.detectionSource?.question !== 'default' ? { questionLocale: result.suggestedQuestionLocale } : {}),
        ...(!localeEdited.current.learning && LOCALES.includes(result?.suggestedLearningLocale) && result?.detectionSource?.learning !== 'default' ? { learningLocale: result.suggestedLearningLocale } : {}),
      }));
      if (!LOCALES.includes(result?.suggestedQuestionLocale) || !LOCALES.includes(result?.suggestedLearningLocale)
        || Object.values(result?.detectionSource || {}).includes('default')) setNotice(text('defaultLocale'));
    } catch {
      if (sequence !== detectionSequence.current) return;
      setNotice(text('defaultLocale'));
    } finally {
      if (sequence === detectionSequence.current) setDetecting(false);
    }
  };

  const addDraft = (draft, editingCard = null) => {
    const result = validateComposerContent(draft, { requireSingleCard: Boolean(editingCard) });
    if (!result.isValid) { setError(formatComposerError(result.error, language)); return false; }
    if (isVocabulary && result.cards.some(card => card.cardType === 'reading' || card.readingSegments || card.dialogTurns)) {
      setError(t('vocabulary.flatOnly', language)); return false;
    }
    if (!editingCard && cards.length + result.cards.length > cardLimit) { setError(text('cardLimit')); return false; }
    const parsed = result.cards.map((card) => {
      const clientId = `${clientPrefix}-${++clientSequence.current}`;
      return { ...editableCardContent(card), clientId };
    });
    const next = editingCard
      ? cards.map((card) => cardIdentity(card) === cardIdentity(editingCard)
        ? { ...editableCardContent(parsed[0]), ...(editingCard.id ? { id: editingCard.id } : { clientId: editingCard.clientId }) }
        : card)
      : [...cards, ...parsed];
    setCards(next);
    setError('');
    if (!originalCards.length && !cards.length && mode !== 'edit') suggestLocales(next);
    return true;
  };

  const review = () => {
    setError('');
    if (!form.noteTitle.trim()) { setError(text('nameRequired')); return; }
    if (isMarket && !form.description.trim()) { setError(text('descriptionRequired')); return; }
    if (isMarket && (!form.difficulty || Number(form.difficulty) < 1 || Number(form.difficulty) > 5)) { setError(text('difficultyRequired')); return; }
    if (bulkText.trim()) { setError(text('pendingText')); return; }
    if (!cards.length && (isMarket || mode !== 'edit')) { setError(text('cardsRequired')); return; }
    if (cards.length > cardLimit) { setError(text('cardLimit')); return; }
    setReviewPage(0);
    setReviewing(true);
  };

  const save = async () => {
    if (pending || savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        ...(isVocabulary ? { studyKind } : {}),
        noteTitle: form.noteTitle.trim(), description: form.description.trim(),
        difficulty: form.difficulty === '' ? null : Number(form.difficulty),
        // Metadata-only vocabulary edits must not re-save thousands of words.
        cards: isVocabulary && mode === 'edit' && !orderChanged && !changes.added.length && !changes.modified.length && !changes.deleted.length
          ? [] : cards.map(cardPayload),
        deletedCardIds: changes.deleted.filter((card) => card.id).map((card) => card.id),
        ...(coverFile ? { coverFile } : {}), ...(removeCover ? { removeCover: true } : {}),
      };
      const fingerprint = JSON.stringify({ ...payload, coverFile: undefined });
      if (!lastSaveOperation.current || lastSaveOperation.current.fingerprint !== fingerprint || lastSaveOperation.current.coverFile !== coverFile) {
        lastSaveOperation.current = { fingerprint, coverFile, operationId: uuidv4() };
      }
      const result = await onSave({ ...payload, operationId: lastSaveOperation.current.operationId });
      // Only a committed save can bypass the unsaved-changes confirmation.
      navigation.saved(() => onSaved?.(result));
    } catch (requestError) {
      setError(requestError.message || text('saveFailed'));
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const requestCancel = () => {
    if (pending) return;
    if (dirty) setConfirmCancel(true);
    else onCancel?.();
  };
  const keepEditing = () => { setConfirmCancel(false); navigation.stay(); };
  const discard = () => { setConfirmCancel(false); navigation.leave(onCancel); };
  useImperativeHandle(ref, () => ({ requestCancel }));
  const session = authService.getSession?.();
  const source = initialNote.sourceType || initialNote.source || (initialNote.sourceMarketListingId ? 'market' : initialNote.sourcePresetId ? 'official' : 'personal');
  const author = initialNote.authorName || initialNote.authorNickname || (typeof initialNote.author === 'string' ? initialNote.author : initialNote.author?.nickname) ||
    (source === 'personal' || source === 'custom' ? session?.nickname || session?.name || session?.user?.nickname || text('you') : text('sourceUnknown'));
  const visibleCards = appendOnly ? cards.filter((card) => !originalCards.some((original) => cardIdentity(original) === cardIdentity(card))) : cards;
  const pageCount = Math.max(1, Math.ceil(visibleCards.length / 50));
  const safePage = Math.min(cardPage, pageCount - 1);
  const displayCards = isVocabulary ? visibleCards.slice(safePage * 50, (safePage + 1) * 50) : visibleCards;
  const moveCard = (identity, amount) => {
    const index = cards.findIndex(card => cardIdentity(card) === identity);
    if (index < 0 || index + amount < 0 || index + amount >= cards.length) return;
    const next = [...cards];
    [next[index], next[index + amount]] = [next[index + amount], next[index]];
    moveFocus.current = { identity, amount };
    setCardPage(Math.floor((index + amount) / 50));
    setCards(next);
    setNotice(t('vocabulary.cardMoved', language, { current: index + amount + 1, total: cards.length }));
  };
  const registerMoveButton = (identity, amount, node) => {
    const key = `${identity}:${amount}`;
    if (node) moveButtons.current.set(key, node);
    else moveButtons.current.delete(key);
  };

  return <div className="note-editor" aria-busy={pending}>
    <fieldset className="note-editor-fields" disabled={pending || reviewing}>
      <legend>{text('metadata')}</legend>
      <div className="note-editor-provenance"><span>{text('source')}: {text(source === 'preset' || source === 'official' ? 'official' : source === 'market' ? 'market' : 'personal')}</span><span>{text('author')}: {author}</span></div>
      <label htmlFor={`${fieldId}-title`}><span>{text('title')}</span><input id={`${fieldId}-title`} value={form.noteTitle} maxLength={100} onChange={(event) => update('noteTitle', event.target.value)} readOnly={appendOnly} /></label>
      {!appendOnly && <>
        <label htmlFor={`${fieldId}-description`}><span>{text('description')} {!isMarket && <small>{text('optional')}</small>}</span><textarea id={`${fieldId}-description`} value={form.description} maxLength={1000} rows={3} onChange={(event) => update('description', event.target.value)} /></label>
        <div className="note-editor-metadata-row">
          <label htmlFor={`${fieldId}-difficulty`}><span>{text('difficulty')}</span><select id={`${fieldId}-difficulty`} value={form.difficulty} onChange={(event) => update('difficulty', event.target.value)}><option value="">{text('unspecified')}</option>{[1, 2, 3, 4, 5].map((value) => <option key={value} value={value}>{value}</option>)}</select></label>
          {['question', 'learning'].map((side) => <label key={side} htmlFor={`${fieldId}-${side}`}>
            <span>{t(`noteComposer.confirm.${side}Locale`, language)}</span>
            <select id={`${fieldId}-${side}`} value={form[`${side}Locale`]} disabled={localesLocked} onChange={(event) => update(`${side}Locale`, event.target.value)}>
              {LOCALES.map((locale) => <option key={locale} value={locale}>{t(`noteComposer.locales.${locale}`, language)}</option>)}
            </select>
          </label>)}
        </div>
        {localesLocked ? <small>{text('lockedLocales')}</small> : <button type="button" className="composer-tool-button" onClick={() => suggestLocales()} disabled={!cards.length || detecting}>{text(detecting ? 'detecting' : 'detect')}</button>}
        {notice && <p role="status">{notice}</p>}
        <div className="note-editor-cover"><span>{text('cover')}</span><CoverUploadWidget
          noteId={initialNote.noteId || initialNote.id || fieldId}
          currentCoverUrl={!removeCover && !currentCoverPreview ? initialNote.coverUrl : null}
          currentCoverPreview={!removeCover ? currentCoverPreview : null}
          onFileSelect={(file) => { setCoverFile(file); setRemoveCover(!file && Boolean(initialNote.coverUrl || currentCoverPreview)); }}
          labels={{ add: t('market.form.addImage'), change: t('market.form.changeImage'), remove: t('market.form.removeImage'), select: t('market.form.selectImage'), preview: t('market.form.coverPreview'), formats: t('market.form.imageFormats'), invalidType: t('market.errors.imageType'), tooLarge: t('market.errors.imageSize') }}
        /></div>
        {!isMarket && <label className="note-editor-enabled"><input type="checkbox" checked={form.isEnabled} onChange={(event) => update('isEnabled', event.target.checked)} />{t('noteComposer.page.practiceState', language)}</label>}
      </>}
    </fieldset>

    <fieldset className="note-editor-fields" disabled={pending || reviewing}>
      <legend>{text('cards')} <span>{cards.length}/{cardLimit}</span></legend>
      <p>{text(appendOnly ? 'appendOnly' : 'editHelp')}</p>
      <div className="note-composer-toolbar">
        <button type="button" className="composer-tool-button primary-tool" onClick={() => setBuilder({ card: null })}><Plus size={18} aria-hidden="true" />{t('noteComposer.page.addOneCard', language)}</button>
        <button type="button" className="composer-tool-button" onClick={async () => {
          try { await navigator.clipboard.writeText(isVocabulary ? [CARD_DRAFT_TYPES.SIMPLE, CARD_DRAFT_TYPES.NORMAL].map(getCardTemplate).join('\n\n') : ALL_CARD_TEMPLATES); setCopied(true); }
          catch { setError(t('noteComposer.errors.copyFailed', language)); }
        }}><Copy size={17} aria-hidden="true" />{t(copied ? 'noteComposer.page.copied' : 'noteComposer.page.copyFormats', language)}</button>
        <a className="composer-tool-button" href="https://www.aastudio.co.kr/manual/" target="_blank" rel="noreferrer"><BookOpen size={17} aria-hidden="true" />{t('noteComposer.page.manual', language)}<ExternalLink size={13} aria-hidden="true" /></a>
      </div>
      <details className="note-editor-bulk" open={bulkText ? true : undefined}>
        <summary>{text('append')}</summary>
        <p>{text('appendHelp')}</p>
        <label htmlFor={`${fieldId}-content`}>{t('noteComposer.page.contentLabel', language)}<textarea id={`${fieldId}-content`} value={bulkText} spellCheck={false} rows={7} onChange={(event) => { setBulkText(event.target.value); setError(''); }} /></label>
        <button type="button" className="composer-tool-button" onClick={() => { if (addDraft(bulkText)) setBulkText(''); }}><Plus size={17} aria-hidden="true" />{text('append')}</button>
      </details>
      {visibleCards.length ? <ol className="note-editor-card-list" start={isVocabulary ? safePage * 50 + 1 : 1}>{displayCards.map((card, localIndex) => {
        const index = isVocabulary ? safePage * 50 + localIndex : localIndex;
        return <li key={cardIdentity(card)}>
        <header><span>{index + 1}. {t(`noteComposer.types.${cardPreview(card).draftType}`, language)}</span><div>
          {isVocabulary && <>
            <button ref={node => registerMoveButton(cardIdentity(card), -1, node)} type="button" className="composer-icon-button" aria-label={`${t('vocabulary.moveCardEarlier', language)} ${index + 1}`} disabled={index === 0 || pending} onClick={() => moveCard(cardIdentity(card), -1)}><ArrowUp size={17} aria-hidden="true" /></button>
            <button ref={node => registerMoveButton(cardIdentity(card), 1, node)} type="button" className="composer-icon-button" aria-label={`${t('vocabulary.moveCardLater', language)} ${index + 1}`} disabled={index === cards.length - 1 || pending} onClick={() => moveCard(cardIdentity(card), 1)}><ArrowDown size={17} aria-hidden="true" /></button>
          </>}
          <button type="button" className="composer-icon-button" aria-label={`${text('editCard')} ${index + 1}`} onClick={() => setBuilder({ card })}><Pencil size={17} aria-hidden="true" /></button>
          <button type="button" className="composer-icon-button" aria-label={`${text('deleteCard')} ${index + 1}`} onClick={() => setDeleteCandidate(card)}><Trash2 size={17} aria-hidden="true" /></button>
        </div></header>
        <CardDraftPreview card={cardPreview(card)} />
      </li>; })}</ol> : <p className="note-editor-empty">{text('empty')}</p>}
      {isVocabulary && pageCount > 1 && <nav className="library-pagination" aria-label={t('vocabulary.page', language)}>
        <button type="button" disabled={safePage === 0 || pending} onClick={() => setCardPage(safePage - 1)}>{t('vocabulary.previousPage', language)}</button>
        <span>{safePage + 1} / {pageCount}</span>
        <button type="button" disabled={safePage === pageCount - 1 || pending} onClick={() => setCardPage(safePage + 1)}>{t('vocabulary.nextPage', language)}</button>
      </nav>}
    </fieldset>
    {error && !reviewing && <p className="composer-page-error" role="alert">{error}</p>}
    <footer className="note-editor-actions">
      <button type="button" className="composer-button secondary" onClick={requestCancel} disabled={pending}>{t('common.cancel', language)}</button>
      <button type="button" className="composer-button primary" onClick={review} disabled={pending || detecting}><Check size={18} aria-hidden="true" />{text('changes')}</button>
    </footer>

    <CardBuilderDialog isOpen={Boolean(builder)} suspended={navigation.blocked} initialCard={builder?.card || null} allowedTypes={isVocabulary ? [CARD_DRAFT_TYPES.SIMPLE, CARD_DRAFT_TYPES.NORMAL] : undefined} onCancel={() => setBuilder(null)} onAdd={(draft) => { if (addDraft(draft, builder?.card)) setBuilder(null); }} />
    <ConfirmDialog isOpen={Boolean(deleteCandidate) && !navigation.blocked}
      title={text('deleteTitle')} message={text('deleteHelp')} tone="danger"
      confirmLabel={text('remove')} cancelLabel={t('common.cancel', language)}
      onCancel={() => setDeleteCandidate(null)}
      onConfirm={() => { setCards((current) => current.filter((card) => cardIdentity(card) !== cardIdentity(deleteCandidate))); setDeleteCandidate(null); }}>
      {deleteCandidate && <CardDraftPreview card={cardPreview(deleteCandidate)} />}
    </ConfirmDialog>
    {reviewing && (!navigation.blocked || pending) && <EditorDialog title={text('changes')} onCancel={() => { if (!pending) setReviewing(false); }}>
      <h3>{form.noteTitle}</h3>
      <p>{form.questionLocale} → {form.learningLocale}</p>
      <dl className="note-editor-review-metadata">
        <div><dt>{text('description')}</dt><dd>{form.description.trim() || text('noDescription')}</dd></div>
        <div><dt>{text('difficulty')}</dt><dd>{form.difficulty || text('unspecified')}</dd></div>
        <div><dt>{text('source')}</dt><dd>{text(source === 'preset' || source === 'official' ? 'official' : source === 'market' ? 'market' : 'personal')}</dd></div>
        <div><dt>{text('author')}</dt><dd>{author}</dd></div>
        {!isMarket && <div><dt>{t('noteComposer.page.practiceState', language)}</dt><dd>{t(form.isEnabled ? 'noteComposer.page.enabled' : 'noteComposer.page.disabled', language)}</dd></div>}
      </dl>
      {(coverFile || removeCover) && <p className="note-editor-cover-change"><strong>{text('cover')}: </strong>{text(coverFile ? 'coverChanged' : 'coverRemoved')}{coverFile ? ` (${coverFile.name})` : ''}</p>}
      <div className="note-editor-change-summary">{['added', 'modified', 'deleted'].map((kind) => <span key={kind}>{text(kind)} <strong>{changes[kind].length}</strong></span>)}</div>
      {orderChanged && <p>{t('vocabulary.orderChanged', language)}</p>}
      {['added', 'modified', 'deleted'].map((kind) => {
        const kindChanges = visibleReviewChanges.filter(change => change.kind === kind);
        return kindChanges.length > 0 && <section key={kind} className="note-editor-review-cards"><h3>{text(kind)}</h3>{kindChanges.map(({ card }) => <CardDraftPreview key={cardIdentity(card)} card={cardPreview(card)} compact />)}</section>;
      })}
      {isVocabulary && reviewPageCount > 1 && <nav className="library-pagination" aria-label={t('vocabulary.reviewPages', language)}>
        <button type="button" disabled={safeReviewPage === 0 || pending} onClick={() => setReviewPage(safeReviewPage - 1)}>{t('vocabulary.previousPage', language)}</button>
        <span>{safeReviewPage + 1} / {reviewPageCount}</span>
        <button type="button" disabled={safeReviewPage === reviewPageCount - 1 || pending} onClick={() => setReviewPage(safeReviewPage + 1)}>{t('vocabulary.nextPage', language)}</button>
      </nav>}
      {!changes.added.length && !changes.modified.length && !changes.deleted.length && <p>{text('unchanged')}</p>}
      {error && <p className="composer-page-error" role="alert">{error}</p>}
      <footer className="note-editor-actions"><button type="button" className="composer-button secondary" disabled={pending} onClick={() => setReviewing(false)}>{text('keepEditing')}</button><button type="button" className="composer-button primary" disabled={pending} onClick={save}>{pending ? t('noteComposer.confirm.saving', language) : text('confirmSave')}</button></footer>
    </EditorDialog>}
    <ConfirmDialog isOpen={(confirmCancel || navigation.blocked) && !pending}
      title={text('cancelTitle')} message={text('cancelHelp')} tone="danger"
      cancelLabel={text('keepEditing')} confirmLabel={text('discard')}
      onCancel={keepEditing} onConfirm={discard} />
  </div>;
}

export default forwardRef(NoteEditor);
